import JustifiFormBaseController from "./justifi_form_base_controller";

export default class extends JustifiFormBaseController {
  static targets = [
    ...JustifiFormBaseController.targets,
    "justifiBankAccountForm",
    "bankAccountForm",
    "accountOwnerName",
    "accountOwnerType",
    "accountType",
    "addressStreet1",
    "addressStreet2",
    "addressCity",
    "addressState",
    "addressPostalCode",
    "addressCountry"
  ];

  static values = {
    accessToken: String,
    accountId: String,
    address: Object,
    entryMethod: String,
    customerId: Number,
    visitId: Number
  };

  getFormTarget() {
    return this.bankAccountFormTarget;
  }

  async submitPaymentMethod(event) {
    event.preventDefault();
    this.clearError();

    if (!this.canSubmit()) {
      return;
    }

    try {
      const data = await this.tokenizePaymentMethod();
      this.processTokenizationResult(data);
    } catch (error) {
      this.handleTokenizationError(error);
    } finally {
      clearTimeout(this.timeoutId);
    }
  }

  canSubmit() {
    if (!this.termsAccepted() || !this.validateRequiredFields()) {
      return false;
    }

    if (!this.startSubmission()) {
      return false;
    }

    if (!this.hasJustifiBankAccountFormTarget) {
      this.displayError("Bank account form component not found. Please refresh the page.");
      this.resetSubmission();
      return false;
    }

    return true;
  }

  async tokenizePaymentMethod() {
    const { accessTokenValue: accessToken, accountIdValue: accountId } = this;

    return await this.withTimeout(
      this.justifiBankAccountFormTarget.tokenize(accessToken, this.buildPaymentMethodData(), accountId),
      3000,
      'Tokenization timeout'
    );
  }

  processTokenizationResult(data) {
    if (data.error) {
      this.displayError(data.error.message || "Payment processing failed. Please try again.");
      this.notifyError(data.error);
      this.resetSubmission();
      return;
    }
    this.handleTokenizeResult(data);
  }

  handleTokenizationError(error) {
    if (error.message.includes('timeout')) {
      // Justifi component already shows validation errors
    } else {
      console.error("Tokenization error:", error);
      this.displayError("An unexpected error occurred. Please try again.");
    }
    this.resetSubmission();
  }

  // Helper: ensure user accepted terms
  termsAccepted() {
    if (this.hasTermsCheckboxTarget && !this.termsCheckboxTarget.checked) {
      // Use data attribute for i18n error message if available
      const errorMessage = this.termsCheckboxTarget.dataset.errorMessage ||
                          "Please accept the payment terms to continue.";
      this.displayError(errorMessage);
      return false;
    }
    return true;
  }

  // Helper: build payload for Justifi
  buildPaymentMethodData() {
    return {
      account_owner_name: this.getTargetValue('accountOwnerName', 'Unknown'),
      account_owner_type: this.getTargetValue('accountOwnerType', 'individual'),
      account_type: this.getTargetValue('accountType', ''),
      country: this.getTargetValue('addressCountry', 'US'),
      currency: 'usd',
      ...this.buildAddressData()
    };
  }

  // Helper: handle successful tokenization response
  handleTokenizeResult(data) {
    if (data.error) {
      this.notifyError(data.error);
      this.resetSubmission();
      return;
    }

    this.appendTokenizationData(data);
    this.submitForm();
  }

  appendTokenizationData(data) {
    this.appendHiddenField("payment_method_id", data.id);
    this.appendHiddenField("entry_method", this.entryMethodValue || "justifi_bank_account_form");
    this.appendHiddenField("account_type", this.getAccountType());
    this.appendHiddenField("account_owner_type", this.getAccountOwnerType());
  }

  getAccountType() {
    return this.hasAccountTypeTarget ? this.accountTypeTarget.value : "checking";
  }

  getAccountOwnerType() {
    return this.hasAccountOwnerTypeTarget ? this.accountOwnerTypeTarget.value : "individual";
  }

  submitForm() {
    if (!this.hasBankAccountFormTarget) {
      this.handleFormSubmissionError("Bank account form target not found");
      return;
    }

    try {
      this.bankAccountFormTarget.requestSubmit();
    } catch (error) {
      this.handleFormSubmissionError("Form submission error", error);
    }
  }

  handleFormSubmissionError(message, error = null) {
    if (error) {
      console.error(`${message}:`, error);
    } else {
      console.error(message);
    }
    this.displayError("Unable to submit form. Please refresh and try again.");
    this.resetSubmission();
  }

  notifyError(error) {
    // Display error to user
    let errorMessage = "An error occurred. Please check your information and try again.";

    if (error.message) {
      errorMessage = error.message;
    } else if (error.code === "routing_number_checksum_failed") {
      errorMessage = "Routing number is invalid";
    }

    // Show error in UI
    this.displayError(errorMessage);

    // Call parent notifyError to handle Honeybadger logging
    super.notifyError(error);
  }

  validateRequiredFields() {
    const rules = [
      { target: "accountOwnerName", message: "Please enter the account owner name.", validator: (v) => v.trim() !== "" },
      { target: "accountOwnerType", message: "Please select the account owner type." },
      { target: "accountType", message: "Please select the account type." }
    ];

    return rules.every(rule => this.validateField(rule));
  }

  validateField(rule) {
    if (!this.hasTarget(rule.target)) {
      return true; // Skip if target doesn't exist
    }

    const value = this.getTargetValue(rule.target, "");
    const isValid = rule.validator ? rule.validator(value) : Boolean(value);

    if (!isValid) {
      this.showFieldError(rule.target, rule.message);
      return false;
    }

    return true;
  }

  hasTarget(targetName) {
    const hasKey = `has${this.capitalize(targetName)}Target`;
    return this[hasKey];
  }

  showFieldError(targetName, message) {
    this.displayError(message);
    const target = this[`${targetName}Target`];
    if (target) {
      target.focus();
    }
  }

  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}
